import React, { useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'i18n'
import { useTextTrivia } from './hooks/useTextTrivia'
import { Question } from './components/Question'
import {
  getDotClassName,
  getDotDifferenceType,
  getDotState,
  getDotStyle,
} from './components/questionProgressDots'
import Countdown from '../shared/Countdown'
import { Difference } from '../shared/DIfference'
import { Box } from '@mui/material'
import analytics from 'global/AnalyticsData'

import ShareIcons from 'components/ShareIcons'
import Carousel from 'components/Carousel/Carousel'
import useEmblaCarousel from 'embla-carousel-react'
import AutoHeight from 'embla-carousel-auto-height'
import './TextTriviaEmblaStep.scss'
import * as styles from './TextTriviaStep.module.scss'
import getClassNameFactory from 'util/getClassNameFactory'
import LegacyRetinaImage from 'components/LegacyRetinaImage'
import ResultScreen from './components/ResultScreen'
import { getUserType } from '../../global/context/sub-contexts/userContext'
import { userTypeEnum } from 'services/user'

const getClassName = getClassNameFactory('TextTrivia', styles)
const getTextTriviaContainerClassName = getClassNameFactory(
  'TextTriviaContainer',
  styles,
)

const getTimerClassName = getClassNameFactory('TextTriviaTimer', styles)

const getQuestionProgressLabelClassName = getClassNameFactory(
  'TextTriviaQuestionProgressLabel',
  styles,
)

export const TextTriviaStep = ({
  brk,
  step,
  onEngage = function () {},
  onAwardPoints = function () {},
  skipButton,
  nextStepButton,
}) => {
  const { t } = useTranslation('textTrivia')
  const { possibleCoinsAwarded } = step
  const {
    questions: questionsProp,
    questionDuration = 60,
    // Beaker toggle (BR-4775). Absent on every break authored before it
    // existed, and those keep the timer -- same default the API applies.
    useTimer = true,
    id: triviaId,
    results,
    introCopy,
  } = step.textTriviaSet

  const isGuest = getUserType() === userTypeEnum.GUEST
  const { sponsoredType = false } = brk || {}
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      watchDrag: false,
      watchResize: true,
    },
    [AutoHeight()],
  )
  const imageContainerRef = useRef(null)

  // Re-measure the carousel once every image that can affect this question's
  // rendered height is actually ready (loaded/errored/already cached), instead
  // of guessing a fixed delay. That's the header image (outside the carousel)
  // *and* every answer thumbnail in the current slide (inside it) -- AutoHeight
  // sets an explicit height on .embla__viewport (overflow: hidden), so a slide
  // image that finishes loading after that measurement gets its bottom/edge
  // clipped by the stale height until something re-measures. The 300ms timer
  // still runs as a fallback for the no-image / never-fires case.
  const reInitWhenImagesReady = useCallback(() => {
    if (!emblaApi) return () => {}

    const fallback = setTimeout(() => {
      emblaApi.reInit()
    }, 300)

    const activeSlide = emblaApi.slideNodes()[emblaApi.selectedScrollSnap()]
    const roots = [imageContainerRef.current, activeSlide].filter(Boolean)
    const imgEls = roots.flatMap(root => Array.from(root.querySelectorAll('img')))

    if (imgEls.length === 0) {
      return () => clearTimeout(fallback)
    }

    let pending = imgEls.filter(img => !img.complete).length

    if (pending === 0) {
      clearTimeout(fallback)
      emblaApi.reInit()
      return () => clearTimeout(fallback)
    }

    const onImageReady = () => {
      pending -= 1
      if (pending <= 0) {
        clearTimeout(fallback)
        emblaApi.reInit()
      }
    }

    const pendingImgEls = imgEls.filter(img => !img.complete)
    pendingImgEls.forEach(img => {
      img.addEventListener('load', onImageReady, { once: true })
      img.addEventListener('error', onImageReady, { once: true })
    })

    return () => {
      clearTimeout(fallback)
      pendingImgEls.forEach(img => {
        img.removeEventListener('load', onImageReady)
        img.removeEventListener('error', onImageReady)
      })
    }
  }, [emblaApi])

  const {
    currentQuestionIndex,
    selectedAnswers,
    totalQuestions,
    handleSelectAnswer,
    handleNextQuestion,
    showRightAnswer,
    currentTime,
    complete,
    handleCheckAnswer,
    isLoading,
    questions,
    startTrivia,
    hasStarted,
    resultScreen,
    showResultScreen,
    hasAnswered,
    wasCorrect,
  } = useTextTrivia({
    questions: questionsProp,
    results,
    maxTime: questionDuration,
    triviaId: triviaId,
    reInitWhenImagesReady,
    onEngage,
    onAwardPoints,
    step,
    analytics,
    trivia: step.textTriviaSet,
    possibleCoinsAwarded,
    useTimer,
  })

  const handleShareClick = ({ platform }) => {
    analytics.stepEvent('step_interaction_trivia_share_option', step, {
      result_id: platform,
      result: platform,
    })
  }

  useEffect(() => {
    // Re-arm on every question change too, not just on mount -- each new
    // slide can bring its own still-loading header/answer images.
    return reInitWhenImagesReady()
  }, [reInitWhenImagesReady, currentQuestionIndex])

  const handleSubmit = answer => {
    handleCheckAnswer(answer)
  }

  const onNextQuestion = () => {
    if (emblaApi) {
      emblaApi.scrollNext()
    }
    handleNextQuestion()
  }

  const getCompleteLabel = () => {
    if (hasAnswered) {
      return wasCorrect
        ? t('feedback.correct')
        : t('feedback.incorrect')
    }
    return t('feedback.outOfTime')
  }

  return (
    <section className={getClassName()}>
      <div
        className={getTextTriviaContainerClassName({
          isSponsored: sponsoredType === 'sponsored',
        })}
      >
        {questions[currentQuestionIndex].image && !showResultScreen && (
          <div
            className={getClassName('imageContainer')}
            ref={imageContainerRef}
          >
            <LegacyRetinaImage
              className={getClassName('image')}
              image={questions[currentQuestionIndex].image}
            />
          </div>
        )}

        {!showResultScreen && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: '13px',
                width: '100%',
                gap: '10px',
                justifyContent: 'center',
              }}
            >
              <span
                className={getQuestionProgressLabelClassName({
                  sponsored: sponsoredType === 'sponsored',
                })}
              >
                {hasStarted
                  ? t('questionCounter', {
                      current: currentQuestionIndex + 1,
                      total: totalQuestions,
                    })
                  : t('questionCount', { count: totalQuestions })}
              </span>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                {hasStarted &&
                  totalQuestions > 1 &&
                  questions.map((question, index) => {
                    const state = getDotState(
                      question,
                      index === currentQuestionIndex,
                      question.wasAnswered,
                    )
                    return (
                      <Difference
                        key={question.id}
                        id={index}
                        // Unhashed class names and a data attribute, so custom
                        // CSS has a stable target.
                        extraClassName={getDotClassName(state)}
                        dataAttributes={{ 'data-trivia-dot': state }}
                        style={getDotStyle(state, sponsoredType)}
                        type={getDotDifferenceType(state)}
                      />
                    )
                  })}
              </Box>
            </Box>
            {useTimer && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                {hasStarted ? (
                  <Countdown
                    sponsoredType={sponsoredType}
                    currentTime={currentTime}
                    complete={complete}
                    completeLabel={getCompleteLabel()}
                    progressLabel={t('findCorrectAnswer')}
                    questionDuration={questionDuration}
                  />
                ) : (
                  <span
                    className={getTimerClassName({
                      sponsored: sponsoredType === 'sponsored',
                    })}
                  >
                    {t('timerInfo', { seconds: questionDuration })}
                  </span>
                )}
              </Box>
            )}
          </>
        )}
        <Carousel
          hasButtons={false}
          hasProgress={false}
          emblaRef={emblaRef}
          emblaApi={emblaApi}
        >
          {questions?.map((question, index) => (
            <Question
              key={index}
              question={question}
              showRightAnswer={showRightAnswer}
              triviaCopy={introCopy}
              onSubmit={handleSubmit}
              onNextQuestion={onNextQuestion}
              selectedAnswers={selectedAnswers}
              onSelectAnswer={handleSelectAnswer}
              isLoading={isLoading}
              sponsoredType={sponsoredType}
              hasStarted={hasStarted}
              currentQuestionIndex={currentQuestionIndex}
              isLastQuestion={index === questions.length - 1}
            />
          ))}
          <ResultScreen
            isGuest={isGuest}
            result={resultScreen}
            socialShare={
              step.socialShareOn && (
                <ShareIcons
                  stepId={step.id}
                  facebookShare={step.facebookShare}
                  twitterShare={step.twitterShare}
                  pinterestShare={step.pinterestShare}
                  emailShare={step.emailShare}
                  emailConfig={{
                    subject: step.shareEmailSubject,
                    body: step.shareEmailBody,
                  }}
                  copyShare={step.copyShare}
                  whatsappShare={step.whatsappShare}
                  shareUrl={brk ? brk.shareUrl : ''}
                  onShareClick={handleShareClick}
                />
              )
            }
          />
        </Carousel>
        <div style={{ zIndex: 'var(--z-dropdown' }}>{skipButton}</div>
        {nextStepButton}
        {!hasStarted && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
            }}
          >
            <button
              onClick={startTrivia}
              className={getClassName('startButton')}
            >
              {t('getStarted')}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

export default TextTriviaStep
